/**
 * Email Verification – Level 1 (dashboard-wt theme)
 */
import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Input from '../common/Input';

const inputClass = '!border-[rgba(255,255,255,0.12)] placeholder:!text-[#737373] focus:!border-[#22c55e]';
const inputStyle = { backgroundColor: '#0a0a0a', color: '#fff' };

const EmailVerification = ({ onVerified }) => {
  const { addEmail, verifyEmail, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState({});

  const handleAddEmail = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setErrors({ email: language === 'fr' ? 'E-mail requis' : 'Email is required' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrors({ email: language === 'fr' ? 'Adresse e-mail invalide' : 'Invalid email address' });
      return;
    }
    setErrors({});
    try {
      await addEmail({ email: trimmed });
      showSuccess(language === 'fr' ? 'Code envoyé à votre e-mail.' : 'Code sent to your email.');
      setStep(2);
    } catch (err) {
      showError(err?.message || err?.errors?.[0] || (language === 'fr' ? 'Envoi échoué' : 'Send failed'));
    }
  };

  const handleResendCode = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setErrors({});
    try {
      await addEmail({ email: trimmed });
      showSuccess(language === 'fr' ? 'Nouveau code envoyé.' : 'New code sent.');
    } catch (err) {
      showError(err?.message || err?.errors?.[0] || (language === 'fr' ? 'Envoi échoué' : 'Send failed'));
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setErrors({ code: language === 'fr' ? 'Code requis' : 'Code is required' });
      return;
    }
    setErrors({});
    try {
      await verifyEmail({ code: trimmed });
      showSuccess(language === 'fr' ? 'E-mail vérifié.' : 'Email verified.');
      setStep(3);
      onVerified?.();
    } catch (err) {
      showError(err?.message || err?.errors?.[0] || (language === 'fr' ? 'Code invalide' : 'Invalid code'));
    }
  };

  // Success
  if (step === 3) {
    return (
      <div className="text-center py-6 px-4">
        <div className="w-14 h-14 rounded-2xl bg-[rgba(34,197,94,0.2)] border border-[rgba(34,197,94,0.4)] flex items-center justify-center text-2xl mx-auto mb-3 text-[#22c55e]">
          ✓
        </div>
        <h3 className="text-lg font-bold text-[#fafafa] mb-1">
          {language === 'fr' ? 'E-mail vérifié' : 'Email verified'}
        </h3>
        <p className="text-[#a3a3a3] text-sm">
          {language === 'fr' ? 'Niveau 1 actif.' : 'Level 1 active.'}
        </p>
      </div>
    );
  }

  // Step 1: Enter email
  if (step === 1) {
    return (
      <div className="space-y-5">
        <h3 className="text-base font-semibold text-[#e5e5e5]">
          {language === 'fr' ? 'Ajouter votre e-mail' : 'Add your email'}
        </h3>
        <p className="text-sm text-[#a3a3a3]">
          {language === 'fr' ? 'Reçus et sécurité renforcée.' : 'Receipts and enhanced security.'}
        </p>
        <form onSubmit={handleAddEmail} className="space-y-4">
          <Input
            label={language === 'fr' ? 'Adresse e-mail' : 'Email address'}
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({}); }}
            placeholder="vous@exemple.com"
            error={errors.email}
            required
            className={inputClass}
            style={inputStyle}
          />
          <button
            type="submit"
            disabled={loading}
            className="dashboard-wt-btn-primary w-full py-3 rounded-lg font-medium"
          >
            {loading ? (language === 'fr' ? 'Envoi...' : 'Sending...') : (language === 'fr' ? 'Envoyer le code' : 'Send code')} →
          </button>
        </form>
      </div>
    );
  }

  // Step 2: Enter code
  return (
    <div className="space-y-5">
      <h3 className="text-base font-semibold text-[#e5e5e5]">
        {language === 'fr' ? 'Code de vérification' : 'Verification code'}
      </h3>
      <p className="text-sm text-[#a3a3a3]">
        {language === 'fr' ? 'Code envoyé à' : 'Code sent to'} <strong className="text-[#fafafa]">{email}</strong>. {language === 'fr' ? 'Entrez-le ci-dessous.' : 'Enter it below.'}
      </p>
      <form onSubmit={handleVerify} className="space-y-4">
        <Input
          label={language === 'fr' ? 'Code à 6 chiffres' : '6-digit code'}
          type="text"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (errors.code) setErrors({}); }}
          placeholder="000000"
          error={errors.code}
          required
          maxLength={6}
          inputMode="numeric"
          className={`text-center text-xl tracking-[0.3em] font-semibold ${inputClass}`}
          style={inputStyle}
        />
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setStep(1)}
            className="dashboard-wt-btn-secondary flex-1 py-3 rounded-lg"
          >
            ← {language === 'fr' ? 'Retour' : 'Back'}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="dashboard-wt-btn-primary flex-1 py-3 rounded-lg font-medium"
          >
            {loading ? (language === 'fr' ? 'Vérification...' : 'Verifying...') : (language === 'fr' ? 'Vérifier' : 'Verify')}
          </button>
        </div>
        <p className="text-center text-xs text-[#737373]">
          {language === 'fr' ? "Pas reçu ?" : "Didn't receive it?"}{' '}
          <button
            type="button"
            onClick={handleResendCode}
            disabled={loading}
            className="text-[#22c55e] hover:underline font-medium"
          >
            {language === 'fr' ? 'Renvoyer le code' : 'Resend code'}
          </button>
        </p>
      </form>
    </div>
  );
};

export default EmailVerification;
